import {
  Menu,
  MessageCircleQuestion,
  Sparkles,
  X,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCRMBilling, useProUser } from "../../hooks/useProData";
import { HeaderAvatar } from "./HeaderAvatar";
import { useProChatStore } from "../../stores/proChat";
import {
  ENGAGEMENT_GROUP,
  LIBRARY_NAV_DESTINATIONS,
  PRIMARY_NAV_DESTINATIONS,
  TEMPLATES_LIBRARY,
  UTILITY_NAV_DESTINATIONS,
  type NavCountKey,
  type NavDestination,
} from "./nav";
import { useMarketplaceGate } from "../../hooks/useMarketplaceGate";
import { useHomeNeedsCount } from "../../hooks/useHomeNeedsCount";
import { SubNav } from "./SubNav";
import { DiscountChip } from "../billing/DiscountChip";
import { Bell } from "../notifications/Bell";
import { FeedbackModal } from "../feedback/FeedbackModal";
import { useMessagesBadge } from "../../hooks/useMessagesBadge";

interface NavCounts {
  /** Home's needs-stream count; the badge is hidden at 0. */
  homeNeeds: number;
  /** Pending marketplace requests. */
  marketplaceRequests: number;
  /** Conversations with unread messages; null = badge not yet seeded. */
  messagesUnread: number | null;
}

function countFor(key: NavCountKey | undefined, counts: NavCounts) {
  if (key === "home_needs") return counts.homeNeeds > 0 ? counts.homeNeeds : null;
  // Like Messages, a zero-requests pill is noise.
  if (key === "marketplace_requests") {
    return counts.marketplaceRequests > 0 ? counts.marketplaceRequests : null;
  }
  // The Messages pill only renders when something is actually unread — an
  // "unread: 0" pill is noise.
  if (key === "messages_unread") {
    return counts.messagesUnread != null && counts.messagesUnread > 0
      ? counts.messagesUnread
      : null;
  }
  return null;
}

function isActivePath(pathname: string, item: NavDestination) {
  const prefixes = item.matchPrefixes ?? [item.to];
  return prefixes.some((prefix) => {
    if (pathname === prefix) return true;
    return pathname.startsWith(`${prefix}/`);
  });
}

export function HeaderNav({
  copilotOpen,
  onToggleCopilot,
}: {
  /** Pro CRM Copilot dock open state (plan 188 W2-M8, task 802) — undefined
   * when the host doesn't wire the copilot (e.g. isolated component tests). */
  copilotOpen?: boolean;
  onToggleCopilot?: () => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  // ONE modal for BOTH openers (desktop icon + mobile sheet row) — the desktop
  // utility cluster is xl:-gated (1280px), so a tablet pro can only reach
  // feedback through the sheet. Same "one dock, two openers" shape the copilot
  // opener already uses in this file.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const messagesUnread = useMessagesBadge();
  // Header Marketplace entry (and its request badge) shows only when the
  // go-live gate is on; "loading" omits it so a resolving flag never flashes it.
  const marketplaceGate = useMarketplaceGate();
  const marketplaceOn = marketplaceGate === "on";
  // Home's badge is the needs-stream count (the same function Home's Needs
  // list uses); Marketplace's is the pending-requests list length. With the
  // gate off no marketplace request is issued.
  const { homeNeeds, marketplaceRequests } = useHomeNeedsCount(marketplaceGate);
  const navCounts: NavCounts = {
    homeNeeds,
    marketplaceRequests,
    messagesUnread,
  };
  const { user, isLoading } = useProUser();
  const billing = useCRMBilling();
  // Earned PRO chip (ak/crm-pricing-page): renders only once a billing
  // summary has actually loaded and the account is on a paid plan — never
  // while loading/unknown (nil-safe) and never for "free". The BETA marker
  // beside it is unrelated and unconditional.
  const showEarnedProBadge =
    billing.data != null && billing.data.plan !== "free";
  const { pathname } = useLocation();
  const hasUnreadChat = useProChatStore(
    (state) => state.hasUnreadAdvisorMessage,
  );
  const markAdvisorMessagesRead = useProChatStore(
    (state) => state.markAdvisorMessagesRead,
  );

  const primaryDestinations = PRIMARY_NAV_DESTINATIONS.filter(
    (item) =>
      (!item.roles || item.roles.includes(user.role)) &&
      (item.id !== "marketplace" || marketplaceOn),
  );
  // Below `xl` the bar carries only Home and Marketplace (with badges).
  const compactDestinations = primaryDestinations.filter(
    (item) => item.id === "home" || item.id === "marketplace",
  );
  const utilityDestinations = UTILITY_NAV_DESTINATIONS.filter(
    (item) => !item.roles || item.roles.includes(user.role),
  );

  // Engagement group is active when any of its sub-nav paths match.
  const engagementActive = ENGAGEMENT_GROUP.matchPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  function handleToggleCopilot() {
    if (!copilotOpen && hasUnreadChat) {
      markAdvisorMessagesRead();
    }
    onToggleCopilot?.();
  }

  return (
    <header className="sticky top-0 z-30 bg-[linear-gradient(185deg,var(--pro-deep)_0%,var(--pro-deep2)_100%)] lg:bg-[linear-gradient(100deg,var(--pro-deep)_0%,var(--pro-deep2)_100%)]">
      <div className="flex h-[54px] items-center gap-2 px-4 md:h-[60px] md:gap-3 md:px-[22px]">
        <div className="flex shrink-0 items-center gap-1.5">
          <Link
            to="/home"
            aria-label="Sill Pro home"
            className="flex shrink-0 items-center gap-2 rounded-md no-underline outline-none focus-visible:ring-2 focus-visible:ring-white/35"
          >
            <img
              src="/sill-wordmark.svg"
              alt="Sill"
              className="block h-[22px] w-auto md:h-[23px]"
            />
            {showEarnedProBadge && (
              <span
                data-testid="earned-pro-badge"
                className="rounded-[5px] border border-[color-mix(in_oklch,var(--pro-ledge)_45%,transparent)] px-1.5 py-[3px] text-[8.5px] font-extrabold leading-none tracking-[0.2em] text-pro-ledge uppercase"
              >
                Pro
              </span>
            )}
          </Link>
        </div>

        {/* Below xl the bar keeps Home and Marketplace (with badges) beside
            the menu button; everything else lives in the menu. */}
        <div
          data-testid="compact-nav"
          className="flex min-w-0 items-center gap-1 xl:hidden"
        >
          {compactDestinations.map((item) => (
            <CompactNavLink key={item.id} item={item} counts={navCounts} />
          ))}
        </div>

        {/* Desktop header (primary nav + right utility cluster) is
            `xl:flex`-gated (1280px). Its natural
            width is ~1080px+, so the md (768px) gate it previously used made
            every Pro surface scroll horizontally on tablets — staging QA
            measured ~313px of overflow at 768x900. Tablets get the same
            hamburger shell as phones. */}
        <nav
          aria-label="Primary"
          className="ml-4 hidden h-full items-stretch xl:flex"
        >
          {primaryDestinations.map((item) => (
            <HeaderNavLink key={item.id} item={item} counts={navCounts} />
          ))}
          {/* Engagement: two-level header group (primary entry + sub-nav row).
              Deliberate trip-wire crossing — see frontend/pro/CLAUDE.md. */}
          <EngagementNavLink active={engagementActive} />
        </nav>

        <div className="ml-auto hidden items-center gap-1.5 xl:flex">
          {/* Hybrid activation nudge, stage "chip": the compact unconsumed-
              incentive reminder that takes over after the pro dismisses the
              UnusedDiscountBanner once. Self-gating (renders null unless the
              nudge stage is "chip"); mutually exclusive with the earned PRO
              badge by construction (chip = free plan, badge = paid). */}
          <DiscountChip />
          {/* Library group — CRM-nav findable resources (Templates). C1 task 341. */}
          <LibraryNavGroup />
          <span className="mx-1 h-4 w-px bg-white/15" aria-hidden />
          {/* Right cluster order (right→left): profile, settings, notification,
              Ask Sill, then secondary utilities (activity). */}
          {utilityDestinations
            .filter((item) => item.id !== "profile" && item.id !== "settings")
            .map((item) => (
              <UtilityLink key={item.id} item={item} />
            ))}
          {onToggleCopilot && (
            <CopilotOpenerPill
              open={!!copilotOpen}
              hasUnread={hasUnreadChat}
              onToggle={handleToggleCopilot}
            />
          )}
          {/* C2: Notification bell — badge driven by useNotifications unread count. */}
          <Bell />
          {/* Feedback sits immediately left of Settings (operator placement):
              it reads as account-adjacent housekeeping rather than a third
              "get help" control beside Ask Sill. */}
          <FeedbackOpener onOpen={() => setFeedbackOpen(true)} />
          {utilityDestinations
            .filter((item) => item.id === "settings")
            .map((item) => (
              <UtilityLink key={item.id} item={item} />
            ))}
          <ProfileMenu user={user} isLoading={isLoading} />
        </div>

        <button
          type="button"
          aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
          className="ml-auto inline-flex size-10 items-center justify-center rounded-lg text-white/85 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/35 xl:hidden"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Engagement sub-nav row: rendered whenever any Engagement path is active. */}
      {engagementActive && (
        <SubNav
          tabs={ENGAGEMENT_GROUP.tabs}
          ariaLabel="Engagement navigation"
        />
      )}
      {/* Marketplace no longer uses SubNav — its secondary nav is the pageSidebar
          (MarketplaceSidebar). No sub-nav row for marketplace paths. */}

      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />

      {mobileOpen && (
        <div className="border-t border-white/10 bg-[linear-gradient(185deg,var(--pro-deep)_0%,var(--pro-deep2)_100%)] px-3 py-3 shadow-lg xl:hidden">
          <nav aria-label="Mobile navigation" className="grid gap-1">
            {/* Mobile: Sill copilot opener (task 818) — the header pill opener
                is `xl:flex`-gated (1280px), so below 1280px this is
                the sole reachable opener. Reuses the same onToggleCopilot handler
                / useCopilotDockOpen state as the desktop pill — no forked
                dock-open state. */}
            {onToggleCopilot && (
              <MobileCopilotOpener
                open={!!copilotOpen}
                hasUnread={hasUnreadChat}
                onToggle={() => {
                  handleToggleCopilot();
                  setMobileOpen(false);
                }}
              />
            )}
            {/* Mobile: discount chip row — the desktop chip is `xl:flex`-gated
                (1280px), so below 1280px this row is the sole reachable surface for the
                post-dismissal incentive reminder (same rationale as the
                mobile copilot opener above). */}
            <DiscountChip mobile />
            {/* Mobile: flatten engagement tabs inline after primary destinations. */}
            {[...primaryDestinations].map((item) => (
              <MobileNavLink
                key={item.id}
                item={item}
                counts={navCounts}
                onNavigate={() => setMobileOpen(false)}
              />
            ))}
            {/* Mobile: Engagement group header (navigates to first tab). */}
            <MobileEngagementGroup
              counts={navCounts}
              onNavigate={() => setMobileOpen(false)}
            />
            {/* Mobile: Library group (Templates). C1 task 341. */}
            <MobileLibraryGroup
              counts={navCounts}
              onNavigate={() => setMobileOpen(false)}
            />
            {utilityDestinations
              .filter(
                (item) => item.id !== "settings" && item.id !== "profile",
              )
              .map((item) => (
                <MobileNavLink
                  key={item.id}
                  item={item}
                  counts={navCounts}
                  onNavigate={() => setMobileOpen(false)}
                />
              ))}
            {/* Mobile: feedback opener, held immediately above Settings to
                mirror the desktop cluster. The desktop icon is xl:flex-gated
                (1280px), so below that this row is the sole reachable entry
                (same rationale as the mobile copilot opener above). */}
            <MobileFeedbackOpener
              onOpen={() => {
                setFeedbackOpen(true);
                setMobileOpen(false);
              }}
            />
            {utilityDestinations
              .filter(
                (item) => item.id === "settings" || item.id === "profile",
              )
              .map((item) => (
                <MobileNavLink
                  key={item.id}
                  item={item}
                  counts={navCounts}
                  onNavigate={() => setMobileOpen(false)}
                />
              ))}
          </nav>
        </div>
      )}
    </header>
  );
}

/**
 * Desktop Library nav group — a text link for the Templates library.
 * C1 task 341: Templates surfaced under a "Library" label in the CRM nav,
 * not as a product tab beside Campaigns/Automations.
 */
function LibraryNavGroup() {
  const { pathname } = useLocation();
  const isActive = LIBRARY_NAV_DESTINATIONS.some((item) =>
    (item.matchPrefixes ?? [item.to]).some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    ),
  );
  return (
    <nav aria-label="Library navigation" className="flex items-center">
      <NavLink
        to={TEMPLATES_LIBRARY.to}
        aria-label="Library — Templates"
        className={({ isActive: routerActive }) =>
          [
            "relative flex h-[38px] items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-medium no-underline outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30",
            routerActive || isActive
              ? "bg-white/10 text-white"
              : "text-white/70",
          ].join(" ")
        }
      >
        <TEMPLATES_LIBRARY.icon size={14} strokeWidth={2} aria-hidden />
        <span>Library</span>
      </NavLink>
    </nav>
  );
}

/**
 * Mobile: Library group — the "Library" label then its destinations, on the
 * same MobileNavLink row as every other drawer entry (no extra indent), so
 * icons and labels form one column. The label's `px-3` matches the rows'.
 */
function MobileLibraryGroup({
  counts,
  onNavigate,
}: {
  counts: NavCounts;
  onNavigate: () => void;
}) {
  return (
    <>
      <div className="px-3 pt-2 pb-1">
        <span className="text-[10.5px] font-bold tracking-[0.18em] text-white/40 uppercase">
          Library
        </span>
      </div>
      {LIBRARY_NAV_DESTINATIONS.map((item) => (
        <MobileNavLink
          key={item.id}
          item={item}
          counts={counts}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
}

/** Desktop header entry for the Engagement group (not a NavLink — it's a group). */
function EngagementNavLink({ active }: { active: boolean }) {
  const { icon: Icon } = ENGAGEMENT_GROUP;
  // The Engagement header entry navigates to the first available sub-nav tab.
  const firstTab = ENGAGEMENT_GROUP.tabs[0];
  if (!firstTab) return null;
  return (
    <Link
      to={firstTab.to}
      aria-label={ENGAGEMENT_GROUP.label}
      aria-current={active ? "page" : undefined}
      className={[
        "relative flex min-w-[88px] items-center justify-center gap-2 px-3 text-sm font-medium no-underline outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/30",
        active ? "font-semibold text-white" : "text-white/70",
      ].join(" ")}
    >
      <Icon size={16} strokeWidth={2} aria-hidden />
      <span>{ENGAGEMENT_GROUP.label}</span>
      {active && (
        <span className="absolute inset-x-3 bottom-0 h-[3px] rounded-t-[3px] bg-pro-ledge" />
      )}
    </Link>
  );
}

/**
 * Mobile: Engagement group — the group label then each tab, on the same
 * MobileNavLink row as every other drawer entry (no extra indent).
 */
function MobileEngagementGroup({
  counts,
  onNavigate,
}: {
  counts: NavCounts;
  onNavigate: () => void;
}) {
  return (
    <>
      <div className="px-3 pt-2 pb-1">
        <span className="text-[10.5px] font-bold tracking-[0.18em] text-white/40 uppercase">
          {ENGAGEMENT_GROUP.label}
        </span>
      </div>
      {ENGAGEMENT_GROUP.tabs.map((tab) => (
        <MobileNavLink
          key={tab.id}
          item={tab}
          counts={counts}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
}

/**
 * Below-`xl` bar link (Home / Marketplace): label + badge, no icon, tight
 * padding so both fit beside the wordmark and the menu button on a phone.
 */
function CompactNavLink({
  item,
  counts,
}: {
  item: NavDestination;
  counts: NavCounts;
}) {
  const resolvedCount = countFor(item.countKey, counts);
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        [
          "flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium no-underline outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30",
          isActive ? "bg-white/10 font-semibold text-white" : "text-white/75",
        ].join(" ")
      }
    >
      <span className="truncate">{item.label}</span>
      {resolvedCount !== null && <CountPill count={resolvedCount} />}
    </NavLink>
  );
}

function HeaderNavLink({
  item,
  counts,
}: {
  item: NavDestination;
  counts: NavCounts;
}) {
  const resolvedCount = countFor(item.countKey, counts);
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        [
          "relative flex min-w-[88px] items-center justify-center gap-2 px-3 text-sm font-medium text-white/70 no-underline outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/30",
          isActive ? "font-semibold text-white" : "",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <item.icon size={16} strokeWidth={2} aria-hidden />
          <span>{item.label}</span>
          {resolvedCount !== null && <CountPill count={resolvedCount} />}
          {isActive && (
            <span className="absolute inset-x-3 bottom-0 h-[3px] rounded-t-[3px] bg-pro-ledge" />
          )}
        </>
      )}
    </NavLink>
  );
}

function UtilityLink({ item }: { item: NavDestination }) {
  return (
    <NavLink
      to={item.to}
      aria-label={item.label}
      className={({ isActive }) =>
        [
          "relative inline-flex size-[38px] items-center justify-center rounded-full text-white/80 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30",
          isActive ? "bg-white/10 text-white" : "",
        ].join(" ")
      }
    >
      <item.icon size={17} strokeWidth={2} aria-hidden />
    </NavLink>
  );
}

/**
 * The one below-`xl` drawer row. Primary/utility destinations, Engagement
 * tabs and Library items all render through it, so every icon and label in
 * the drawer sits in one column. A row with no icon keeps an icon-sized
 * spacer rather than shifting its label left.
 */
interface MobileNavItem {
  to: string;
  label: string;
  icon?: LucideIcon;
  countKey?: NavCountKey;
}

function MobileNavLink({
  item,
  counts,
  onNavigate,
}: {
  item: MobileNavItem;
  counts: NavCounts;
  onNavigate: () => void;
}) {
  const resolvedCount = countFor(item.countKey, counts);
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          "flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/75 no-underline outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30",
          isActive ? "bg-white/13 font-semibold text-white" : "",
        ].join(" ")
      }
    >
      {Icon ? (
        <Icon size={17} strokeWidth={2} aria-hidden className="shrink-0" />
      ) : (
        <span aria-hidden className="size-[17px] shrink-0" />
      )}
      <span>{item.label}</span>
      {resolvedCount !== null && (
        <span className="ml-auto">
          <CountPill count={resolvedCount} />
        </span>
      )}
    </NavLink>
  );
}

function ProfileMenu({
  user,
  isLoading,
}: {
  user: ReturnType<typeof useProUser>["user"];
  isLoading: boolean;
}) {
  return (
    <Link
      to="/profile"
      aria-label="Your profile"
      className="group ml-1 flex items-center gap-2 rounded-full p-1 pr-2 no-underline outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/30"
    >
      <HeaderAvatar initials={user.avatar} />
      {/* Name + org only from 2xl: at exactly xl (1280px) the full bar
          (six nav entries with badges, Library, five icon buttons) leaves no
          room for them and the page scrolled sideways. The avatar circle and
          the "Your profile" label carry the entry below that. */}
      <span
        data-testid="header-profile-text"
        className="hidden max-w-[150px] min-w-0 leading-[1.2] 2xl:block"
      >
        <span className="block truncate text-[12.5px] font-semibold text-white">
          {user.name}
        </span>
        {isLoading && !user.org ? (
          <span
            data-testid="header-org-skeleton"
            aria-hidden
            className="mt-1 block h-[9px] w-[86px] rounded bg-white/[0.18]"
          />
        ) : (
          <span className="block truncate text-[10.5px] text-white/58">
            {user.org}
          </span>
        )}
      </span>
    </Link>
  );
}

/**
 * Sole opener for the Pro CRM Copilot dock (scenario 674): an icon-only spark
 * button (no "Sill" label) with an idle notification dot, sized to match the
 * sibling utility icon buttons (settings, notification). The open state fills
 * with the accent pill background. No auto-open on first visit — this is the
 * only opener.
 */
function CopilotOpenerPill({
  open,
  hasUnread,
  onToggle,
}: {
  open: boolean;
  hasUnread: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={open}
      aria-label={
        open ? "Close Sill" : hasUnread ? "Open Sill, unread chat" : "Open Sill"
      }
      className={
        open
          ? "relative inline-flex size-[38px] items-center justify-center rounded-full bg-pro-ledge text-[oklch(0.18_0.05_165)] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white/30"
          : "relative inline-flex size-[38px] items-center justify-center rounded-full text-white/80 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30"
      }
    >
      <Sparkles size={17} strokeWidth={2} aria-hidden />
      {!open && hasUnread && (
        <span
          aria-hidden
          className="absolute right-2 top-2 size-[7px] rounded-full bg-pro-ledge"
        />
      )}
    </button>
  );
}

/**
 * Mobile copilot opener (scenario 679, task 818): a full-width row inside the
 * mobile nav sheet that toggles the same copilot dock/drawer state as the
 * desktop "Sill" pill. The desktop pill is `xl:flex`-gated (1280px), so a
 * tablet or mobile pro has no other way to reach the copilot
 * without this.
 */
function MobileCopilotOpener({
  open,
  hasUnread,
  onToggle,
}: {
  open: boolean;
  hasUnread: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={open}
      aria-label={
        open ? "Close Sill" : hasUnread ? "Open Sill, unread chat" : "Open Sill"
      }
      className="flex min-h-11 items-center gap-3 rounded-lg bg-white/10 px-3 text-sm font-semibold text-white no-underline outline-none transition-colors hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/30"
    >
      <Sparkles size={17} strokeWidth={2} aria-hidden />
      <span>{open ? "Close Sill" : "Ask Sill"}</span>
      {!open && hasUnread && (
        <span
          aria-hidden
          className="ml-auto size-[7px] rounded-full bg-pro-ledge"
        />
      )}
    </button>
  );
}

/**
 * Feedback opener (desktop) — a utility icon in the header right cluster.
 *
 * Replaces the former "Beta" chip, which conflated a status marker ("this
 * product is unfinished") with an affordance ("click to talk to us"). Removing
 * the marker must not cost the affordance its one-click reach, so feedback
 * keeps a persistent header presence as its own control.
 *
 * Icon matches the consumer app's "Send feedback" button (HelpPage) so the
 * same action reads the same across surfaces. Placement (immediately left of
 * Settings) is pinned by HeaderNav.test.tsx.
 */
function FeedbackOpener({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      data-testid="feedback-opener"
      onClick={onOpen}
      aria-label="Send feedback"
      title="Send feedback"
      className="relative inline-flex size-[38px] items-center justify-center rounded-full text-white/80 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30"
    >
      <MessageCircleQuestion size={17} strokeWidth={2} aria-hidden />
    </button>
  );
}

/**
 * Feedback opener (mobile) — a full-width row in the mobile nav sheet that
 * opens the SAME modal instance as the desktop icon (state lives in
 * HeaderNav). The desktop icon is `xl:flex`-gated (1280px), so without this a
 * tablet or phone pro has no way to send feedback at all.
 */
function MobileFeedbackOpener({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      data-testid="feedback-opener-mobile"
      onClick={onOpen}
      aria-label="Send feedback"
      className="flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-white/75 no-underline outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30"
    >
      <MessageCircleQuestion size={17} strokeWidth={2} aria-hidden />
      <span>Send feedback</span>
    </button>
  );
}

function CountPill({ count }: { count: number }) {
  return (
    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-pro-ledge px-1.5 text-[11px] font-extrabold tabular-nums text-[oklch(0.18_0.05_165)]">
      {count}
    </span>
  );
}

export function useIsNavDestinationActive(
  item: NavDestination,
  pathname: string,
) {
  return isActivePath(pathname, item);
}
